import React from 'react';
import InventoryItem from './InventoryItem';
import './InventoryCategory.css';

/**
 * InventoryCategory Component
 * Displays a category of inventory items
 * 
 * @param {Object} props - Component props
 * @param {string} props.categoryName - Name of the category
 * @param {Array} props.items - List of inventory items in this category
 * @param {Function} props.onQuantityChange - Callback when quantity changes
 * @param {Function} props.onNeededChange - Callback when needed/shortage values change
 * @param {boolean} props.isAdmin - Whether the user has admin privileges
 */
const InventoryCategory = ({ 
  categoryName, 
  items, 
  itemCount,
  onQuantityChange,
  onUnitChange,
  onNeededChange,
  isAdmin
}) => {
  // Get category icon based on name
  const getCategoryIcon = () => {
    const name = categoryName.toLowerCase();
    if (name.includes('coffee')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
          <line x1="6" y1="1" x2="6" y2="4"/>
          <line x1="10" y1="1" x2="10" y2="4"/>
          <line x1="14" y1="1" x2="14" y2="4"/>
        </svg>
      );
    } else if (name.includes('tea')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 8h1a4 4 0 1 1 0 8h-1"/>
          <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/>
          <line x1="6" y1="2" x2="6" y2="4"/>
          <line x1="10" y1="2" x2="10" y2="4"/>
          <line x1="14" y1="2" x2="14" y2="4"/>
        </svg>
      );
    } else if (name.includes('juice')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 2v7c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V2"/>
          <path d="M7 2v20"/>
          <path d="M17 2v20"/>
          <path d="M7 12H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-2"/>
        </svg>
      );
    } else if (name.includes('flavor')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
          <path d="M8.5 8.5v.01"/>
          <path d="M16 15.5v.01"/>
          <path d="M12 12v.01"/>
        </svg>
      );
    } else if (name.includes('additive')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="12" y1="18" x2="12" y2="12"/>
          <line x1="9" y1="15" x2="15" y2="15"/>
        </svg>
      );
    } else {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 7h-9M14 17H5M17 14V3M7 14v9"/>
        </svg>
      );
    }
  };
  
  // Capitalize category name properly
  const displayName = categoryName.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
  return (
    <div className="inventory-category">
      <div className="category-header">
        <div className="category-title-wrapper">
          <div className="category-icon">{getCategoryIcon()}</div>
          <h2 className="category-title">{displayName}</h2>
          <span className="category-count">({itemCount || items.length})</span>
        </div>
      </div>
      <div className="category-items">
        {items.map(item => (
          <InventoryItem
            key={item.id}
            item={item}
            onQuantityChange={(newQuantity) => onQuantityChange(categoryName, item.id, newQuantity)}
            onUnitChange={(newUnit) => onUnitChange(categoryName, item.id, newUnit)}
            onNeededChange={(field, value) => onNeededChange(categoryName, item.id, field, value)}
            isAdmin={isAdmin}
          />
        ))}
      </div>
    </div>
  );
};

export default InventoryCategory;
